import { sumBy } from 'lodash';
import moment from 'moment';
import { default as React, useCallback } from 'react';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { GoogleAdsRequestGq } from '../../../../graphql/projects/models/GoogleAdsRequestGq';
import { indirectSeedTypeToString } from '../../../../graphql/projects/models/IndirectSeedTypeGq';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { ProjectKeywordGq } from '../../../../graphql/projects/models/ProjectKeywordGq';
import { ProjectSerpTrackingRequestGq } from '../../../../graphql/projects/models/ProjectSerpTrackingRequestGq';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import { formatCurrency } from '../../../../logic/otherUtilities/formatting/formatCurrency';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import VoxelLoadingSpinner from '../../../voxel/Animation/VoxelLoadingSpinner';
import VoxelDataTableCell from '../../../voxel/DataTable/VoxelDataTableCell';
import VoxelDataTableRow from '../../../voxel/DataTable/VoxelDataTableRow';
import { FastTagGroup } from '../Shared/TableCellTagGroups';
import { getColumnId } from './KeywordTableColumnPicker/getColumnId';
import { KeywordTableColumn, KeywordTableColumnTypes } from './KeywordTableColumnPicker/models';
import TableCellCheckbox from './TableCells/TableCellCheckbox';
import TableCellDomain from './TableCells/TableCellDomainRank/TableCellDomain';
import TableCellKeyword from './TableCells/TableCellKeyword/TableCellKeyword';
import TableCellMonthPeak from './TableCells/TableCellMonthPeak';
import TableCellQuarterPeak from './TableCells/TableCellQuarterPeak';
import TableCellSERPFeatures from './TableCells/TableCellSERPFeatures';
import TableCellSource from './TableCells/TableCellSource';
import TableCellTagGroup from './TableCells/TableCellTagGroup';
import TableCellTrend from './TableCells/TableCellTrend';
import TableCellVolume from './TableCells/TableCellVolume/TableCellVolume';
import TableCellVolume12months from './TableCells/TableCellVolume12months';
import { TableUIState } from './logic/TableUIState';
import { KeywordFilteringGq } from '../../../../graphql/projects/models/KeywordFilteringGq';
import {
  KeywordSelecting,
  KeywordSelectingGq,
} from '../../../../graphql/projects/models/KeywordSelectingGq';

export interface KeywordTableRowProps {
  project?: ProjectGq;
  user?: CurrentUserGq;
  keyword: ProjectKeywordGq;
  index: number;
  apiCallCollections?: Record<number, GoogleAdsRequestGq>;
  onMouseDown?: (keywordId: number, index: number, selected: boolean) => void;
  onMouseUp?: (index?: number) => void;
  onMouseOver?: (keywordId: number, index: number) => void;
  dragSelected: boolean;
  tableUIState: TableUIState;
  setTableUIState: React.Dispatch<React.SetStateAction<TableUIState>>;
  fastTagGroups: Record<number, FastTagGroup>;
  shownColumns: KeywordTableColumn[];
  rankCtrValues?: Record<string, number>;
  isProgressPage?: boolean;
  areVariantsLoading?: boolean;
  localVariant?: number;
  handleSelectVariant: (keywordId: number, variantId: number) => void;
  trackingRequests?: ProjectSerpTrackingRequestGq[];
  reload: () => Promise<any>;
  selected: boolean;
  setOpenKeywordId: (id: number) => void; // Optimization to only pass selection & filtering in case tag is open - these parameters would otherwise break memoization
  backendSelection?: KeywordSelectingGq; // Optimized - only passed if tag popover is open
  backendFiltering?: KeywordFilteringGq; // Optimized - only passed if tag popover is open
  resetSelection?: () => void; // Optimized - only passed if tag popover is open
}

// The entire row is memoized
const KeywordTableRow: React.FC<KeywordTableRowProps> = React.memo(
  ({
    project,
    keyword,
    onMouseDown,
    onMouseOver,
    onMouseUp,
    index,
    dragSelected,
    apiCallCollections,
    fastTagGroups,
    shownColumns,
    rankCtrValues,
    isProgressPage,
    areVariantsLoading,
    localVariant,
    handleSelectVariant,
    trackingRequests,
    tableUIState,
    setTableUIState,
    reload,
    selected,
    user,
    setOpenKeywordId,
    backendFiltering,
    backendSelection,
    resetSelection,
  }) => {
    // Quick search fix
    const currencyConversionRate = !!project?.currencyConversionRate
      ? project?.currencyConversionRate
      : 1;

    // 12 month vol data
    const dataLast12months = keyword?.months2?.slice(24);
    const yearVolume = sumBy(dataLast12months, (t) => t);

    // Show tag group panel
    const handleShowTagGroupPanel = useCallback(
      (id?: number) =>
        setTableUIState((prev) => {
          if (!id) {
            return { ...prev, shownTagPanelId: undefined, showTagPanel: false };
          }

          if (prev.shownTagPanelId === id && !!id && prev.showTagPanel) {
            return { ...prev, shownTagPanelId: undefined, showTagPanel: false };
          }

          return { ...prev, shownTagPanelId: id, showTagPanel: true };
        }),
      [setTableUIState]
    );

    return (
      <VoxelDataTableRow
        onMouseDown={() => {
          onMouseDown && onMouseDown(keyword.id, index, !selected);
        }}
        onMouseOver={onMouseOver ? () => onMouseOver(keyword.id, index) : undefined}
        onMouseUp={onMouseUp ? () => onMouseUp(index) : undefined}
        className={`cursor-pointer select-none
            ${
              /* Normal */
              !keyword.archived && !selected && !dragSelected
                ? 'bg-white hover:bg-voxel-gray-50'
                : ''
            }
            ${
              /* Selected */
              selected && !dragSelected ? 'bg-blue-50/10 hover:bg-blue-100/50' : ''
            },
            ${
              /* Being dragged over */
              dragSelected ? '!bg-voxel-blue-50/40 hover:bg-voxel-blue-50/40' : ''
            },
            ${
              /* Archived */
              keyword.archived && !selected && !dragSelected
                ? '!bg-voxel-gray-50 opacity-70 hover:bg-voxel-gray-100'
                : ''
            },
        `}
      >
        <TableCellCheckbox selected={selected} />
        {shownColumns.map((column) => {
          if (!column) return null;
          if (!(column as any).type && (column as any).type !== 0) return null;

          const key = getColumnId(column);
          if (column.type === KeywordTableColumnTypes.ArchiveTime)
            return (
              <VoxelDataTableCell align={'left'} key={key}>
                {!keyword.archiveTime
                  ? ''
                  : moment.utc(keyword.archiveTime).local().format('YYYY-MM-DD hh:mm:ss')}
              </VoxelDataTableCell>
            );

          if (column.type === KeywordTableColumnTypes.Keyword)
            return (
              <TableCellKeyword
                key={key}
                keyword={keyword}
                areVariantsLoading={areVariantsLoading}
                localVariant={localVariant}
                handleSelectVariant={handleSelectVariant}
              />
            );
          // Translation columns
          if (column.type === KeywordTableColumnTypes.EnglishTranslation)
            return (
              <VoxelDataTableCell key={key}>
                {keyword.translationEnglishQueued && !keyword.translationEnglishCompleted ? (
                  <VoxelLoadingSpinner />
                ) : (
                  keyword.translationEnglish
                )}
              </VoxelDataTableCell>
            );
          if (column.type === KeywordTableColumnTypes.TranslationFrom)
            return (
              <VoxelDataTableCell align={'left'} key={key}>
                {keyword.translatedFromKeyword}
              </VoxelDataTableCell>
            );
          // Keyword data kolumns
          if (column.type === KeywordTableColumnTypes.Volume)
            return (
              <TableCellVolume
                key={key}
                keyword={keyword}
                longMonthDisabled={!!project?.longMonthDisabled}
                endMonth={project.keywordMonth36End}
                startMonth={project.keywordMonth36End}
              />
            );
          if (column.type === KeywordTableColumnTypes.TranslationFromVolume)
            return (
              <VoxelDataTableCell align={'right'} key={key} type="number">
                {formatNumber(keyword.translatedFromVolume)}
              </VoxelDataTableCell>
            );
          if (column.type === KeywordTableColumnTypes.VolumeTrend)
            return (
              <TableCellTrend
                key={key}
                keyword={keyword}
                longMonthDisabled={!!project?.longMonthDisabled}
              />
            );
          if (column.type === KeywordTableColumnTypes.VolumeMonthPeak)
            return <TableCellMonthPeak keyword={keyword} key={key} />;
          if (column.type === KeywordTableColumnTypes.VolumeQuarterPeak)
            return <TableCellQuarterPeak keyword={keyword} key={key} />;
          if (column.type === KeywordTableColumnTypes.VolumeLast12months)
            return dataLast12months.map((m, index) => (
              <TableCellVolume12months
                key={index}
                keyword={keyword}
                yearVolume={yearVolume}
                monthVolume={m}
              />
            ));
          if (column.type === KeywordTableColumnTypes.Cpc)
            return (
              <VoxelDataTableCell align="right" key={key} type="number">
                {keyword.fetchingNewVolume ? (
                  <div className="flex flex-row justify-end">
                    <VoxelLoadingSpinner />
                  </div>
                ) : (
                  formatCurrency(
                    project?.currency ? project?.currency : 'SEK',
                    formatNumber((keyword.cPC * currencyConversionRate) / 1000000, 2)
                  )
                )}
              </VoxelDataTableCell>
            );

          if (column.type === KeywordTableColumnTypes.CpcBidHigh)
            return (
              <VoxelDataTableCell align="right" key={key} type="number">
                {keyword.fetchingNewVolume ? (
                  <div className="flex flex-row justify-end">
                    <VoxelLoadingSpinner />
                  </div>
                ) : (
                  formatCurrency(
                    project?.currency ? project?.currency : 'SEK',
                    formatNumber((keyword.bidHigh * currencyConversionRate) / 1000000, 2)
                  )
                )}
              </VoxelDataTableCell>
            );

          if (column.type === KeywordTableColumnTypes.CpcBidLow)
            return (
              <VoxelDataTableCell align="right" key={key} type="number">
                {keyword.fetchingNewVolume ? (
                  <div className="flex flex-row justify-end">
                    <VoxelLoadingSpinner />
                  </div>
                ) : (
                  formatCurrency(
                    project?.currency ? project?.currency : 'SEK',
                    formatNumber((keyword.bidLow * currencyConversionRate) / 1000000, 2)
                  )
                )}
              </VoxelDataTableCell>
            );
          if (column.type === KeywordTableColumnTypes.Competition)
            return (
              <VoxelDataTableCell align="right" key={key} type="number">
                {keyword.fetchingNewVolume ? (
                  <div className="flex flex-row justify-end">
                    <VoxelLoadingSpinner />
                  </div>
                ) : (
                  formatNumber(keyword.competition)
                )}
              </VoxelDataTableCell>
            );
          if (column.type === KeywordTableColumnTypes.SeedKeyword)
            return <VoxelDataTableCell key={key}>{keyword.indirectSeed}</VoxelDataTableCell>;

          if (column.type === KeywordTableColumnTypes.SeedType)
            return (
              <VoxelDataTableCell key={key}>
                {indirectSeedTypeToString(keyword.indirectSeedType)}
              </VoxelDataTableCell>
            );

          if (column.type === KeywordTableColumnTypes.KeywordSearch)
            return (
              <TableCellSource
                key={key}
                keyword={keyword}
                apiCall={apiCallCollections[keyword.requestId]}
              />
            );
          // Rank columns
          /* if (column.type === KeywordTableColumnTypes.RankStatus)
            return (
              <TableCellRankFetchStatus
                key={key}
                serpStatus={keyword.serpStatus}
                date={keyword.domainSerpResultTime ?? keyword.serpResultTime}
                componentType={'tableCell'}
              />
            );
 */
          if (column.type === KeywordTableColumnTypes.SerpFeatures)
            return (
              <TableCellSERPFeatures
                keyword={keyword}
                projectDomainId={project.domains?.find((d) => d?.type === DomainTypeGq.PROJECT)?.id}
                serpStatus={keyword.serpStatus}
              />
            );
          if (column.type === KeywordTableColumnTypes.Rank) {
            return (
              <TableCellDomain
                key={key}
                project={project}
                keyword={keyword}
                domainId={column.id}
                showRankColor={tableUIState.colorRanks}
                showUrl={!!column.url}
                showTraffic={!!column.traffic}
                rankCtrValues={rankCtrValues}
                isProgressPage={isProgressPage}
                trackingRequests={trackingRequests}
                serpStatus={keyword.serpStatus}
                user={user}
              />
            );
          }
          if (column.type === KeywordTableColumnTypes.TagCount)
            return (
              <VoxelDataTableCell key={key} type="number" align="right">
                {keyword.tags.length}
              </VoxelDataTableCell>
            );
          // Tag group columns
          if (column.type === KeywordTableColumnTypes.TagGroup) {
            return (
              <TableCellTagGroup
                key={key}
                keyword={keyword}
                keywordTagIds={keyword.tags}
                tagGroups={fastTagGroups}
                columnTagGroupId={column.id}
                reload={reload}
                tagPanelOpen={
                  tableUIState.showTagPanel && tableUIState.shownTagPanelId === column.id
                }
                openTagPanel={handleShowTagGroupPanel}
                onTagPopoverOpen={(open: boolean) =>
                  setOpenKeywordId(open ? keyword.id : undefined)
                }
                backendFiltering={backendFiltering}
                backendSelection={backendSelection}
                resetSelection={resetSelection}
              />
            );
          }
          return null;
        })}

        <VoxelDataTableCell />
        <VoxelDataTableCell />
      </VoxelDataTableRow>
    );
  }
);

export default KeywordTableRow;
